<template>
  <div class="discogs-container">
    <!-- Authentication prompt -->
    <div v-if="!hasCompletedAuth" class="auth-prompt">
      <h2>Connect to Discogs</h2>
      <p>You need to connect your Discogs account to view your collection.</p>
      <button class="connect-button" @click="navigateToLogin">Connect with Discogs</button>
    </div>

    <!-- Loading state -->
    <div v-else-if="isLoading" class="loading-grid">
      <div v-for="n in 8" :key="n" class="loading-card">
        <div class="loading-cover"></div>
        <div class="loading-content">
          <div class="loading-title"></div>
          <div class="loading-artist"></div>
        </div>
      </div>
    </div>

    <!-- Couldn't load and nothing cached to show -->
    <div v-else-if="loadError && !releases.length" class="no-results">
      <h3>Couldn't load your collection</h3>
      <p>{{ loadError }}</p>
      <button class="clear-search" @click="loadCollection({ force: true })">Retry</button>
    </div>

    <!-- Search results or collection -->
    <div v-else>
      <h1 class="visually-hidden">{{ searchAllDiscogs ? 'Discogs search results' : 'Your collection' }}</h1>

      <!-- One slim row: filters on the left; count and sort on the right -->
      <div class="toolbar">
        <div v-if="!searchAllDiscogs && releases.length" class="filters" role="group" aria-label="Filters">
          <filter-menu
            v-for="facet in facets"
            :key="facet.key"
            :label="facet.label"
            :options="facet.options"
            :selected-count="facet.selectedCount"
            @toggle="value => toggleFilter(facet.key, value)"
            @clear="clearGroup(facet.key)"
          />
          <button
            type="button"
            class="toggle-chip"
            :class="{ active: needsTags }"
            :aria-pressed="needsTags"
            @click="needsTags = !needsTags"
          >
            Needs tags <span class="chip-count">{{ needsTagsCount }}</span>
          </button>
          <button v-if="activeFilterCount" type="button" class="link-btn" @click="clearFilters">
            Clear filters
          </button>
        </div>

        <div class="toolbar-end">
          <p v-if="searchAllDiscogs" class="stats" aria-live="polite">
            {{ searchResults.length }} result{{ searchResults.length !== 1 ? 's' : '' }}
          </p>
          <p v-else-if="releases.length" class="stats" aria-live="polite">
            <template v-if="hasSearched">{{ searchResults.length }} of {{ releases.length }}</template>
            <template v-else>{{ releases.length }} records</template>
            <span v-if="isRefreshing"> · updating…</span>
          </p>
          <button v-if="hasSearched && searchQuery" type="button" class="link-btn" @click="clearSearch">
            Clear search
          </button>
          <label v-if="!searchAllDiscogs && releases.length" class="sort">
            <span class="visually-hidden">Sort records</span>
            <select v-model="sort">
              <option v-for="option in SORTS" :key="option.key" :value="option.key">{{ option.label }}</option>
            </select>
          </label>
        </div>
      </div>

      <!-- Nothing matches the search or filters; the toolbar above stays so filters can be changed -->
      <div v-if="hasSearched && searchResults.length === 0" class="no-results">
        <svg class="no-results-icon" xmlns="http://www.w3.org/2000/svg" height="64" viewBox="0 0 24 24" width="64">
          <path d="M0 0h24v24H0V0z" fill="none"/>
          <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5
            5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5
            11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14zM7 9h5v1H7z"/>
        </svg>
        <h3>No results found</h3>
        <p v-if="collectionQuery && !searchAllDiscogs">
          Nothing in your collection matches "{{ collectionQuery }}"{{ activeFilterCount ? ' with these filters' : '' }}
        </p>
        <p v-else-if="activeFilterCount && !searchAllDiscogs">No records match these filters.</p>
        <p v-else>We couldn't find any matches for "{{ searchQuery }}"</p>
        <div class="empty-actions">
          <button v-if="activeFilterCount && !searchAllDiscogs" class="clear-search" @click="clearFilters">
            Clear filters
          </button>
          <button v-if="searchQuery" class="clear-search" @click="clearSearch">Clear search</button>
        </div>
      </div>

      <!-- Results grid -->
      <div v-else class="releases-grid">
        <div v-for="card in displayedCards" :key="card.key" class="grid-item">
          <disco-card
            :title="card.title"
            :id="card.id"
            :cover_art_url="card.coverImage"
            :year="card.year"
            :master_id="card.masterId"
            :artists="card.artists"
            :labels="card.labels"
            :genres="card.genres"
          ></disco-card>
        </div>
      </div>
    </div>

    <!-- Loading indicator for infinite scroll -->
    <div v-if="isSearching" class="infinite-loading">
      <div class="loading-spinner"></div>
    </div>
  </div>
</template>

<script setup>
import {
  ref, onMounted, computed, watch, nextTick, defineProps,
} from 'vue';
import { useRouter } from 'vue-router';
import DiscoCard from '@/components/DiscoCard.vue';
import FilterMenu from '@/components/FilterMenu.vue';
import useCollectionView, { SORTS } from '@/composables/useCollectionView';
import useInfiniteScroll from '@/composables/useInfiniteScroll';
import useGlobalSearch from '@/composables/useGlobalSearch';
import useAuth from '@/composables/useAuth';
import useCollection from '@/composables/useCollection';

// How many collection cards to render at a time as you scroll
const PAGE_SIZE = 24;

const emit = defineEmits(['clear-search']);

const props = defineProps({
  searchQuery: {
    type: String,
    default: '',
  },
  searchAllDiscogs: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();
const navigateToLogin = () => {
  router.push('/login');
};

const { hasCompletedAuth } = useAuth();

// The whole collection comes from the shared, cached store; paging and searching it is local
const {
  releases, isLoading, isRefreshing, error: loadError, loadCollection,
} = useCollection();

// Searching all of Discogs still goes through the backend
const {
  searchResults: globalSearchResults,
  isSearching,
  hasSearched: hasGlobalSearched,
  performSearch: performGlobalSearch,
  resetSearch: resetGlobalSearch,
  loadMoreResults: loadMoreGlobalResults,
  hasMoreResults: hasMoreGlobalResults,
} = useGlobalSearch();

// Query for searching your own collection; cleared locally by the Clear button
const collectionQuery = ref('');
const visibleCount = ref(PAGE_SIZE);

// Search text from the header, then the filters and sort chosen on this page
const {
  visible: collectionMatches, facets, sort, needsTags, needsTagsCount, activeFilterCount,
  toggle: toggleFilter, clearGroup, clearFilters,
} = useCollectionView(releases, collectionQuery);

const hasSearched = computed(() => (props.searchAllDiscogs
  ? hasGlobalSearched.value
  : !!collectionQuery.value || activeFilterCount.value > 0));
const searchResults = computed(() => (props.searchAllDiscogs ? globalSearchResults.value : collectionMatches.value));

// One card shape for collection records and Discogs search results
const fromCollection = record => ({
  key: `c${record.instanceId}`,
  title: record.title,
  id: record.releaseId,
  coverImage: record.coverImage,
  year: record.originalYear || record.pressingYear,
  masterId: String(record.masterId || ''),
  artists: record.artists,
  labels: record.labels || [],
  genres: record.genres || [],
});

const fromSearch = result => ({
  key: `s${result.id}`,
  title: result.title,
  id: result.id,
  coverImage: result.cover_image,
  year: Number(result.year) || null,
  masterId: String(result.master_id || ''),
  artists: [],
  labels: Array.isArray(result.label) ? result.label : [],
  genres: [],
});

const displayedCards = computed(() => (props.searchAllDiscogs && hasGlobalSearched.value
  ? globalSearchResults.value.map(fromSearch)
  : collectionMatches.value.slice(0, visibleCount.value).map(fromCollection)));

// Also empties the search box in the header, which owns the text
const clearSearch = () => {
  collectionQuery.value = '';
  resetGlobalSearch();
  emit('clear-search');
};

watch([() => props.searchQuery, () => props.searchAllDiscogs], ([query, allDiscogs]) => {
  if (allDiscogs) {
    // The filters are hidden in this mode, so don't leave them applied out of sight
    clearFilters();
    collectionQuery.value = '';
    if (query) performGlobalSearch(query);
    else resetGlobalSearch();
  } else {
    resetGlobalSearch();
    collectionQuery.value = query;
  }
}, { immediate: true });

// A new filter, sort or search starts again from the top of the list
watch(collectionMatches, () => { visibleCount.value = PAGE_SIZE; });

const hasMore = () => (props.searchAllDiscogs
  ? hasMoreGlobalResults()
  : visibleCount.value < collectionMatches.value.length);

const fetchNextPage = async () => {
  if (props.searchAllDiscogs) {
    await loadMoreGlobalResults();
  } else {
    visibleCount.value += PAGE_SIZE;
  }
};

onMounted(() => {
  if (hasCompletedAuth.value) loadCollection();
});

watch(hasCompletedAuth, isAuth => {
  if (isAuth) loadCollection();
});

// Grow the rendered list as the user nears the bottom
const { check: checkInfiniteScroll } = useInfiniteScroll(fetchNextPage, hasMore, () => isSearching.value);
// Keep filling until the page can scroll (large screens may fit a whole batch)
watch(() => displayedCards.value.length, () => nextTick(checkInfiniteScroll));
</script>

<style scoped>
.discogs-container {
  max-width: var(--page-max);
  margin: 0 auto;
  padding: var(--space-md) var(--page-gutter) var(--space-xl);
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-sm) var(--space-md);
  margin-bottom: var(--space-md);
}

.filters,
.toolbar-end {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-sm);
}

.toolbar-end {
  margin-left: auto;
  gap: var(--space-md);
}

.toggle-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: var(--color-surface-light);
  color: var(--color-text);
  border: 1px solid transparent;
  border-radius: var(--border-radius-pill);
  padding: 0.4rem 0.9rem;
  font-size: var(--font-size-sm);
}

.toggle-chip:hover {
  background: var(--color-surface-lighter);
}

.toggle-chip.active {
  border-color: var(--color-primary);
  background: rgba(29, 185, 84, 0.15);
}

.chip-count {
  color: var(--color-text-secondary);
  font-size: var(--font-size-xs);
}

.link-btn {
  background: none;
  border: none;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  padding: 0.4rem 0.25rem;
  text-decoration: underline;
}

.link-btn:hover {
  color: var(--color-text);
}

.toggle-chip:focus-visible,
.link-btn:focus-visible,
.sort select:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

.sort {
  display: flex;
}

.sort select {
  background: var(--color-surface-light);
  color: var(--color-text);
  border: none;
  border-radius: var(--border-radius-pill);
  padding: 0.4rem 0.9rem;
  font-size: var(--font-size-sm);
}

.empty-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--space-sm);
}

.stats {
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  margin: 0;
}

.clear-search {
  background: none;
  border: 1px solid var(--color-text-secondary);
  color: var(--color-text);
  padding: 0.4rem 1rem;
  border-radius: 50px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s;
}

.clear-search:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: var(--color-text);
}

.releases-grid {
  display: grid;
  gap: 1.5rem 1.25rem;
  grid-template-columns: repeat(auto-fill, minmax(min(155px, 100%), 1fr));
  margin-bottom: 2rem;
}

.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 4rem 1rem;
  background-color: rgba(255, 255, 255, 0.02);
  border-radius: var(--border-radius);
  margin: 2rem 0;
}

.no-results-icon {
  fill: var(--color-text-secondary);
  margin-bottom: 1rem;
  opacity: 0.7;
}

.no-results h3 {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.5rem;
  color: var(--color-text);
}

.no-results p {
  font-size: 1rem;
  color: var(--color-text-secondary);
  margin: 0 0 1.5rem;
}

.loading-grid {
  display: grid;
  gap: 1.25rem;
  grid-template-columns: repeat(auto-fill, minmax(min(155px, 100%), 1fr));
}

.loading-card {
  display: flex;
  flex-direction: column;
  background: var(--color-surface);
  border-radius: var(--border-radius);
  padding: 1rem;
  height: 100%;
}

.loading-cover {
  width: 100%;
  padding-bottom: 100%;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: calc(var(--border-radius) - 2px);
  margin-bottom: 1rem;
  position: relative;
  overflow: hidden;
}

.loading-cover::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.08), transparent);
  animation: shine 1.5s infinite;
}

.loading-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.loading-title {
  height: 1rem;
  width: 90%;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
}

.loading-artist {
  height: 0.8rem;
  width: 60%;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
}

@keyframes shine {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.infinite-loading {
  display: flex;
  justify-content: center;
  margin: 2rem 0;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  border-top: 3px solid var(--color-primary);
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.auth-prompt {
  text-align: center;
  padding: 3rem 2rem;
  background-color: rgba(255, 255, 255, 0.02);
  border-radius: var(--border-radius);
  max-width: 500px;
  margin: 100px auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.auth-prompt h2 {
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: var(--color-text);
}

.auth-prompt p {
  margin-bottom: 2rem;
  color: var(--color-text-secondary);
}

.connect-button {
  padding: 0.9rem 2rem;
  background-color: var(--color-primary);
  color: white;
  border: none;
  border-radius: 500px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
}

.connect-button:hover {
  background-color: var(--color-primary-light);
  transform: scale(1.05);
}

@media (max-width: 479px) {
  .toggle-chip {
    padding: 0.35rem 0.65rem;
  }

  .releases-grid,
  .loading-grid {
    gap: 0.75rem;
  }
}

@media (min-width: 480px) {
  .releases-grid,
  .loading-grid {
    grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  }
}

@media (min-width: 1024px) {
  .releases-grid {
    gap: 2rem 1.5rem;
  }
}
</style>
